import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Play, Heart, BookmarkPlus, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VideoCard = ({ video, showProgress = false, progress = 0 }) => {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeader } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleClick = () => {
    navigate(`/video/${video.id}`, { state: { video } });
  };

  const handleAddToWatchlist = async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error('Please sign in to add to watchlist');
      navigate('/login');
      return;
    }
    try {
      await axios.post(`${API}/user/watchlist`, {
        video_id: video.id,
        title: video.title,
        thumbnail: video.thumbnail,
        duration: video.duration,
        added_at: new Date().toISOString()
      }, getAuthHeader());
      toast.success('Added to watchlist');
    } catch (err) {
      if (err.response?.status === 400) {
        toast.info('Already in watchlist');
      } else {
        toast.error('Failed to add to watchlist');
      }
    }
  };

  const handleAddToFavorites = async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error('Please sign in to add to favorites');
      navigate('/login');
      return;
    }
    try {
      await axios.post(`${API}/user/favorites`, {
        video_id: video.id,
        title: video.title,
        thumbnail: video.thumbnail,
        duration: video.duration,
        added_at: new Date().toISOString()
      }, getAuthHeader());
      toast.success('Added to favorites');
    } catch (err) {
      if (err.response?.status === 400) {
        toast.info('Already in favorites');
      } else {
        toast.error('Failed to add to favorites');
      }
    }
  };

  const touchStartRef = React.useRef({ x: 0, y: 0 });

  const handleTouchStart = (e) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  };

  const handleTouchEnd = (e) => {
    // Only trigger click if touch didn't move much (it's a tap, not a scroll)
    if (e.target.closest('button')) return; // Let button clicks through
    
    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
    
    // If movement is less than 10px, consider it a tap
    if (deltaX < 10 && deltaY < 10) {
      handleClick();
    }
  };

  return (
    <div
      className="video-card group cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      data-testid={`video-card-${video.id}`}
    >
      <div className="video-thumbnail relative aspect-video rounded-lg overflow-hidden">
        {!imageLoaded && (
          <div className="absolute inset-0 skeleton" />
        )}
        <img
          src={video.thumbnail || 'https://images.unsplash.com/photo-1721040530907-646692053af8?w=600'}
          alt={video.title}
          className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1721040530907-646692053af8?w=600';
            setImageLoaded(true);
          }}
          loading="lazy"
        />
        
        {/* Overlay on hover */}
        <div className={`video-overlay absolute inset-0 bg-black/60 flex items-center justify-center transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
            <Play className="w-6 h-6 text-white fill-white ml-1" />
          </div>
        </div>

        {/* Action buttons on hover */}
        <div className={`absolute top-2 right-2 flex gap-2 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <button
            onClick={handleAddToFavorites}
            className="p-2 bg-black/60 backdrop-blur rounded-full hover:bg-white/20 transition-colors"
            data-testid={`favorite-btn-${video.id}`}
          >
            <Heart className="w-4 h-4 text-white" />
          </button>
          <button
            onClick={handleAddToWatchlist}
            className="p-2 bg-black/60 backdrop-blur rounded-full hover:bg-white/20 transition-colors"
            data-testid={`watchlist-btn-${video.id}`}
          >
            <BookmarkPlus className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Duration badge */}
        <span className="duration-badge">{video.duration}</span>

        {/* Progress bar for continue watching */}
        {showProgress && progress > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-700">
            <div 
              className="h-full bg-white" 
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      <div className="p-3">
        <h3 className="text-sm font-medium text-white line-clamp-2 mb-2 group-hover:text-gray-300 transition-colors">
          {video.title}
        </h3>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            {video.views}
          </span>
          {video.rating && (
            <span className="text-green-500">{video.rating}%</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoCard;
